/**
 * Measurement unit helpers. All storage-place distances are stored internally
 * in millimeters; these helpers convert to/from the units shown in the UI.
 */
export type MeasurementUnit = 'km' | 'm' | 'dm' | 'cm' | 'mm';

export const measurementUnits: MeasurementUnit[] = ['km', 'm', 'dm', 'cm', 'mm'];

interface UnitInfo {
  symbol: string;
  /** Millimeters per one unit. */
  factor: number;
  /** Fraction digits shown for this unit (keeps at least 1 mm resolution). */
  maxFractionDigits: number;
}

const unitInfo: Record<MeasurementUnit, UnitInfo> = {
  km: { symbol: 'km', factor: 1_000_000, maxFractionDigits: 6 },
  m: { symbol: 'm', factor: 1_000, maxFractionDigits: 3 },
  dm: { symbol: 'dm', factor: 100, maxFractionDigits: 2 },
  cm: { symbol: 'cm', factor: 10, maxFractionDigits: 1 },
  mm: { symbol: 'mm', factor: 1, maxFractionDigits: 3 },
};

export const MM_PER_M = 1000;

function round(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

export function unitSymbol(unit: MeasurementUnit): string {
  return unitInfo[unit].symbol;
}

export function unitFractionDigits(unit: MeasurementUnit): number {
  return unitInfo[unit].maxFractionDigits;
}

export function metersToMillimeters(meters: number): number {
  return round(meters * MM_PER_M, 3);
}

export function millimetersToMeters(millimeters: number): number {
  return round(millimeters / MM_PER_M, 6);
}

/** Converts a millimeter value into the given display unit. */
export function toDisplayUnit(millimeters: number, unit: MeasurementUnit): number {
  const info = unitInfo[unit];
  return round(millimeters / info.factor, info.maxFractionDigits);
}

/** Converts a value entered in the given unit back to millimeters. */
export function fromDisplayUnit(value: number, unit: MeasurementUnit): number {
  return round(value * unitInfo[unit].factor, 3);
}

/** Formats a millimeter value as a string in the given unit including its symbol. */
export function formatMeasurement(millimeters: number, unit: MeasurementUnit): string {
  return `${toDisplayUnit(millimeters, unit)} ${unitInfo[unit].symbol}`;
}
